// Admin hook for managing products per boutique
import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { ensureBrowserCompatibleImage } from '../lib/imageUtils';

export interface ProductSize {
  label: string;
  sku: string;
  stockCount: number;
  reservedCount: number;
  committedCount: number;
  returnedCount: number;
  lowStockAlert: boolean;
  available: boolean;
}

export interface AdminProduct {
  id: string;
  name: string;
  brand: string;
  brandId: string;
  description: string;
  price: number;
  compareAtPrice?: number | null;
  saleSchedule?: {
    startDate: string;
    endDate: string;
  } | null;
  images: string[];
  sizes: ProductSize[] | Array<{ label: string; available: boolean }>;
  category: string;
  tags: string[];
  active: boolean;
  lowStockThreshold?: number;
  trackInventory?: boolean;
  createdAt: any;
}

export interface NewProductData {
  name: string;
  description: string;
  price: number;
  compareAtPrice?: number | null;
  saleSchedule?: {
    startDate: string;
    endDate: string;
  } | null;
  category: string;
  tags: string[];
  sizes: ProductSize[];
  images: string[];
  imageFiles?: File[];
}

// Fetch ALL products for a boutique (including inactive) — admin only
export function useAdminProducts(brandId: string | null) {
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!brandId) {
      setProducts([]);
      setLoading(false);
      return;
    }

    // No active filter — admin sees everything
    const q = query(
      collection(db, 'products'),
      where('brandId', '==', brandId)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as AdminProduct[];
        setProducts(data);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching products for admin:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [brandId]);

  return { products, loading, error };
}

// Toggle product active/inactive
export async function toggleProductActive(product: AdminProduct): Promise<void> {
  const ref = doc(db, 'products', product.id);
  await updateDoc(ref, { active: !product.active });

  // Log to audit
  try {
    await addDoc(collection(db, 'admin_logs'), {
      boutiqueId: product.brandId,
      boutiqueName: product.brand,
      action: product.active ? 'hide_product' : 'show_product',
      changes: {
        productId: { from: product.id, to: product.id },
        productName: { from: product.name, to: product.name },
        active: { from: product.active, to: !product.active },
      },
      createdAt: serverTimestamp(),
    });
  } catch (_) {
    // audit failure shouldn't block
  }
}

// Update product fields
export async function updateProduct(
  productId: string,
  updates: Partial<Omit<AdminProduct, 'id' | 'brandId' | 'brand' | 'createdAt'>>
): Promise<void> {
  const ref = doc(db, 'products', productId);
  await updateDoc(ref, updates);
}

// Delete a product entirely
export async function deleteProduct(product: AdminProduct): Promise<void> {
  await deleteDoc(doc(db, 'products', product.id));

  try {
    await addDoc(collection(db, 'admin_logs'), {
      boutiqueId: product.brandId,
      boutiqueName: product.brand,
      action: 'delete_product',
      changes: {
        productId: { from: product.id, to: null },
        productName: { from: product.name, to: null },
      },
      createdAt: serverTimestamp(),
    });
  } catch (_) {
    // audit failure shouldn't block
  }
}

// Upload product image to Firebase Storage (converts HEIC to JPEG automatically)
export async function uploadProductImage(
  brandId: string,
  file: File,
  fileName?: string
): Promise<string> {
  const compatible = await ensureBrowserCompatibleImage(file);
  const name = fileName || `${Date.now()}_${compatible.name}`;
  const storageRef = ref(storage, `products/${brandId}/${name}`);
  const snapshot = await uploadBytes(storageRef, compatible);
  return getDownloadURL(snapshot.ref);
}

// Add a new product for a business
export async function addProduct(
  brandId: string,
  brandName: string,
  data: NewProductData
): Promise<string> {
  // Fetch business inventory settings to inherit lowStockThreshold and trackInventory
  const businessSnap = await getDoc(doc(db, 'businesses', brandId));
  const businessData = businessSnap.exists() ? businessSnap.data() : null;
  const inventorySettings = businessData?.inventorySettings;
  const lowStockThreshold = inventorySettings?.lowStockThreshold ?? 5;
  const trackInventory = inventorySettings?.trackInventory ?? true;

  // Upload any image files first
  let imageUrls = [...data.images];
  if (data.imageFiles && data.imageFiles.length > 0) {
    const uploadPromises = data.imageFiles.map((file) =>
      uploadProductImage(brandId, file)
    );
    const uploaded = await Promise.all(uploadPromises);
    imageUrls = [...imageUrls, ...uploaded];
  }

  // Build sizes with SKUs, applying the business's lowStockThreshold
  const sizes: ProductSize[] = data.sizes.map((s) => {
    const sellable = (s.stockCount || 0) - (s.reservedCount || 0);
    return {
      ...s,
      sku: s.sku || `${brandId}_${data.name.replace(/\s+/g, '_').toUpperCase()}_${s.label}`,
      reservedCount: s.reservedCount || 0,
      committedCount: s.committedCount || 0,
      returnedCount: s.returnedCount || 0,
      lowStockAlert: trackInventory && sellable > 0 && sellable <= lowStockThreshold,
      available: s.stockCount > 0,
    };
  });

  const isOnSale = Boolean(
    data.compareAtPrice && data.compareAtPrice > data.price
  );

  const productDoc: Record<string, any> = {
    name: data.name.trim(),
    brand: brandName,
    brandId,
    description: data.description.trim(),
    price: data.price,
    compareAtPrice: data.compareAtPrice ?? null,
    saleSchedule: data.saleSchedule ?? null,
    sale: isOnSale,
    category: data.category,
    tags: data.tags,
    sizes,
    images: imageUrls,
    active: true,
    lowStockThreshold,
    trackInventory,
    createdAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, 'products'), productDoc);

  // Audit log
  try {
    await addDoc(collection(db, 'admin_logs'), {
      boutiqueId: brandId,
      boutiqueName: brandName,
      action: 'add_product',
      changes: {
        productId: { from: null, to: docRef.id },
        productName: { from: null, to: data.name },
        price: { from: null, to: data.price },
      },
      createdAt: serverTimestamp(),
    });
  } catch (_) {
    // audit failure shouldn't block
  }

  return docRef.id;
}

// Update stock count for a specific size
export async function updateSizeStock(
  productId: string,
  sizes: ProductSize[]
): Promise<void> {
  const ref = doc(db, 'products', productId);
  await updateDoc(ref, { sizes });
}
